/**
 * Wedding car in Cannes — EN twin of `slugs/voiture-mariage-cannes`.
 *
 * Same intent gap on the English side: "wedding chauffeur car rental cannes",
 * "wedding limousine service cannes" and "corporate limousine service cannes"
 * all show impressions with no dedicated page behind them.
 *
 * Every figure comes from the real registries — VEHICLES disposalPackages
 * (the quote engine) and RENTAL_VEHICLES prices (the product pages). Nothing
 * invented: no decoration, no champagne, no cancellation terms, none of which
 * are documented anywhere in the project.
 */
import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const weddingCarCannes: ChauffeurPage = {
  slug: "wedding-car-cannes",
  title: "Wedding car in Cannes — with or without a chauffeur",
  subtitle:
    "A convertible for the couple, a chauffeur-driven saloon, a van or a minibus for the guests: one agency for the whole logistics of the day on the French Riviera.",
  keywords: [
    "wedding car Cannes",
    "wedding car rental Cannes",
    "wedding chauffeur French Riviera",
    "wedding limousine Cannes",
    "guest shuttle Cannes",
  ],
  hero: {
    title: "Wedding car in Cannes — with or without a chauffeur",
    subtitle:
      "The convertible you drive yourselves, or the chauffeur who runs the day while you enjoy yours. Often both.",
    keywords: [
      "wedding car Cannes",
      "wedding car rental",
      "wedding chauffeur French Riviera",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Prestige chauffeur-driven car for a wedding in Cannes",
    badges: [
      "With or without a chauffeur",
      "Up to 16 passengers",
      "5.0/5 · 38 Google reviews",
    ],
  },
  benefits: {
    heading: "Why book the big day with us",
    items: [
      {
        title: "Both trades, one agency",
        description:
          "We rent vehicles and we provide chauffeurs. No need to line up a rental company for the couple's car and a separate chauffeur service for the guests: one contact, one quote.",
      },
      {
        title: "A chauffeur on hold, not a single ride",
        description:
          "With hourly hire, the car and its chauffeur stay with you for the duration you choose. Town hall, photographs, ceremony, reception: each step follows the last without a new price every time.",
      },
      {
        title: "From compact to prestige convertible",
        description:
          "Mercedes CLE, Audi A5, BMW 4 Series, Maserati GranCabrio convertibles: the couple's car comes from the same fleet we rent all year round, at the same published prices.",
      },
      {
        title: "The guests too",
        description:
          "V-Class for 7 passengers, VIP Sprinter for up to 16: hotel, ceremony and reception shuttles are handled by the same chauffeurs, within the same booking.",
      },
    ],
  },
  services: {
    heading: "Our wedding options",
    services: [
      {
        title: "Chauffeur hire for the couple",
        text: "A chauffeur and car for a set duration, from 3 hours. Around €240 for 3 hours in E-Class, €450 in S-Class, €360 in V-Class. A full 8-hour day ranges from €600 to €1,100 depending on the class.",
      },
      {
        title: "The couple's car, self-drive",
        text: "Daily rental of a convertible or prestige saloon: from €350 for a Mercedes CLE Cabriolet or Audi A5 Cab, €450 for a BMW 4 Series Cab, €850 for a Maserati GranCabrio.",
      },
      {
        title: "Guest shuttles",
        text: "V-Class (7 seats) or VIP Sprinter (16 seats) with chauffeur for hotel ⇄ ceremony ⇄ reception rotations, late evenings included.",
      },
      {
        title: "Airport transfers for family",
        text: "Meet and greet at Nice airport for family and witnesses arriving the day before, at a price fixed in advance — €100 in E-Class to Cannes, €130 in V-Class.",
      },
    ],
  },
  locations: {
    heading: "Where we operate",
    items: [
      {
        name: "Cannes",
        description:
          "Town hall, Croisette hotels, hillside villas and inland estates.",
      },
      {
        name: "Antibes & Juan-les-Pins",
        description: "Cap d'Antibes, Port Vauban and wine estates.",
      },
      {
        name: "Saint-Paul-de-Vence & Grasse",
        description: "Bastides, Provençal farmhouses and inland estates.",
      },
      {
        name: "Saint-Tropez & the gulf",
        description: "Ramatuelle, Gassin and the private beaches.",
      },
      {
        name: "Monaco & Nice",
        description: "Palaces, reception venues and Nice airport.",
      },
    ],
  },
  generic: {
    heading: "What does a wedding car cost in Cannes?",
    subheading:
      "Our rates are the ones published on the site all year round — there is no separate “wedding” pricing.",
    paragraphs: [
      "Two pricing logics apply, depending on whether you drive. If you want the wheel, it is a daily rental: from €250 for an Abarth 500 to €350 for a Mercedes CLE or Audi A5 convertible, €450 for a BMW 4 Series Cab, and up to €850 for a Maserati GranCabrio. Those models include 200 km per day, comfortably enough for a day between town hall, photo location and reception.",
      "If you would rather be driven, it is hourly hire: the chauffeur and car are held for you for a chosen duration, three hours minimum. A four-hour half day costs €320 in E-Class, €480 in V-Class (7 seats) and €600 in S-Class. An eight-hour day runs from €600 to €1,100. Beyond the selected package, each extra hour is billed between €80 and €150 depending on the class.",
      "Many couples combine the two: the convertible on rental for themselves and the photographs, and a V-Class or VIP Sprinter with chauffeur to move the guests. Both sit in the same quote.",
    ],
    bullets: [
      "Chauffeur hire from €240 (3 hours, E-Class)",
      "Convertible rental from €350 per day",
      "Up to 16 passengers per vehicle with the VIP Sprinter",
      "Rated 5.0/5 on 38 Google reviews, trading since 2022",
    ],
  },
  process: {
    heading: "How we organise it",
    steps: [
      "Tell us the date, the venues (town hall, ceremony, reception) and how many people need moving.",
      "We propose the right combination: rental only, chauffeur hire, or both, with the durations spelled out.",
      "You receive a written quote listing every vehicle, every duration and the matching price.",
      "On the day, the vehicles are on site at the agreed times and the chauffeur stays reachable throughout.",
    ],
  },
  faq: [
    {
      q: "How much does a wedding car cost in Cannes?",
      a: "Self-drive, a convertible starts at €350 per day (Mercedes CLE Cabriolet, Audi A5 Cab), €450 for a BMW 4 Series Cab and €850 for a Maserati GranCabrio, with 200 km included. With a chauffeur, hourly hire starts at €240 for 3 hours in E-Class and €450 in S-Class; an eight-hour day runs from €600 to €1,100 depending on the class.",
    },
    {
      q: "Can we book the chauffeur for the whole day?",
      a: "Yes. Chauffeur hire is booked in blocks: 3, 4, 8, 10, 12 or 14 hours. The chauffeur and car stay at your disposal for the whole duration, with no new price between steps. The minimum is three hours.",
    },
    {
      q: "How many guests can you carry?",
      a: "Up to 7 passengers per Mercedes V-Class and up to 16 per VIP Sprinter. Several vehicles can be assigned to the same booking for a cortège or for rotations between hotel, ceremony and reception.",
    },
    {
      q: "Do you operate outside Cannes?",
      a: "Yes, across the whole French Riviera: Antibes, Juan-les-Pins, Saint-Paul-de-Vence, Grasse, Nice, Monaco, as well as Saint-Tropez and the gulf. Journeys with no fixed price are quoted per kilometre, from €2.60/km.",
    },
    {
      q: "Can we rent the couple's car and book a chauffeur-driven vehicle at once?",
      a: "Yes — it is the most common combination. We run both trades, rental and chauffeur transport: both appear in the same quote and are coordinated by the same contact.",
    },
    {
      q: "When should we book?",
      a: "As early as possible for a wedding in June, July, August or September: those are the months when the fleet and the chauffeurs are most in demand on the Riviera. Every booking is confirmed in writing.",
    },
  ],
  relatedLinks: [
    { label: "Chauffeur hire rates", href: "/en/chauffeur#mise-a-disposition" },
    { label: "Browse the car catalogue", href: "/en/location/vehicules" },
    { label: "Cannes chauffeur rates", href: "/en/chauffeur/cannes" },
    { label: "VIP minibus up to 16 seats", href: "/en/chauffeur/vip-minibus" },
    { label: "Request a quote", href: "/en/contact" },
  ],
  cta: {
    title: "Let's talk about your day",
    text: "Give us the date, the venues and the number of guests, and we come back with a detailed quote, vehicle by vehicle.",
    primaryHref: "/en/contact",
    primaryLabel: "Request a quote",
    secondaryHref: "/en/reservation",
    secondaryLabel: "Book online",
  },
  seo: {
    title: "Wedding car in Cannes — with or without a chauffeur",
    description:
      "Wedding cars in Cannes: convertible rental from €350/day, chauffeur hire from €240, guest shuttles up to 16 seats. Detailed quote, one agency for the day.",
    canonical: "/en/chauffeur/wedding-car-cannes",
  },
};

export default weddingCarCannes;
